import type { OrgUnitSummary } from '@nadoutong/contracts';
import { useState } from 'react';
import type { FormEvent } from 'react';
import {
  useArchiveOrgUnit,
  useCreateOrgUnit,
  useOrgUnits,
  useUpdateOrgUnit,
} from '../../api/useOrgs.ts';
import { PanelError } from '../../components/PanelError.tsx';
import { useToast } from '../../components/Toast.tsx';
import { hasPermission, useSession } from '../../hooks/useSession.tsx';
import { useT } from '../../i18n/index.ts';
import {
  Button,
  Chip,
  ConfirmDialog,
  Drawer,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Panel,
  Select,
  TBody,
  Table,
  TableSkeleton,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';
import { formatTeamApiError } from './shared.ts';

/**
 * `/team/units` — org units (部门) as their own routed page (epic #897
 * PR 7). Create and edit share one small `Drawer` (name + parent);
 * archive — which used to mutate on a single click — now always runs
 * through a danger `ConfirmDialog`. Success confirmations toast; form
 * and archive failures render inline.
 */

type DrawerState =
  | { readonly mode: 'create' }
  | { readonly mode: 'edit'; readonly unit: OrgUnitSummary };

const ROOT = 'root';

export function TeamUnits(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { user } = useSession();
  const orgId = user?.orgId ?? undefined;
  const canReadMembers = hasPermission(user, 'org:members:read');
  const canManage = hasPermission(user, 'org:members:manage');

  const unitsQ = useOrgUnits(canReadMembers ? orgId : undefined);
  const archiveM = useArchiveOrgUnit();

  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<OrgUnitSummary | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  if (!canReadMembers) {
    return (
      <section data-testid="team-units-page" className="flex flex-col gap-4">
        <PageHeader title={t('team.units.title')} testId="team-units-header" />
        <Panel caption={t('team.units.title')} padded>
          <p className="m-0 text-dense text-paper-low" data-testid="team-forbidden">
            {t('team.forbidden')}
          </p>
        </Panel>
      </section>
    );
  }

  const units = unitsQ.data?.units ?? [];

  async function handleArchive(): Promise<void> {
    if (orgId === undefined || archiveTarget === null) return;
    setArchiveError(null);
    try {
      await archiveM.mutateAsync({ orgId, orgUnitId: archiveTarget.id });
      setArchiveTarget(null);
      toast.success(t('team.units.toast.archived'));
    } catch (err) {
      setArchiveError(formatTeamApiError(err, t('team.units.archive_error')));
    }
  }

  return (
    <section data-testid="team-units-page" className="flex flex-col gap-4">
      <PageHeader
        title={t('team.units.title')}
        sub={t('team.units.subtitle')}
        testId="team-units-header"
        actions={
          canManage ? (
            <Button
              variant="primary"
              onClick={() => setDrawer({ mode: 'create' })}
              data-testid="team-units-create-button"
            >
              {t('team.units.create')}
            </Button>
          ) : undefined
        }
      />

      <Panel
        caption={t('team.units.title')}
        count={unitsQ.data === undefined ? undefined : units.length}
        testId="team-units-panel"
      >
        {unitsQ.isLoading ? (
          <TableSkeleton rows={3} testId="team-units-loading" />
        ) : unitsQ.isError ? (
          <div className="p-4">
            <PanelError
              message={formatTeamApiError(unitsQ.error, t('team.units.error.load'))}
              retryLabel={t('team.retry')}
              onRetry={() => {
                void unitsQ.refetch();
              }}
              testId="team-units-error"
            />
          </div>
        ) : units.length === 0 ? (
          <EmptyState
            testId="team-units-empty"
            title={t('team.units.empty.title')}
            hint={t('team.units.empty.hint')}
            action={
              canManage ? (
                <Button
                  variant="secondary"
                  onClick={() => setDrawer({ mode: 'create' })}
                  data-testid="team-units-empty-create"
                >
                  {t('team.units.create')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Table className="rounded-none border-0" testId="team-units-table">
            <THead>
              <Tr interactive={false}>
                <Th>{t('team.units.column.name')}</Th>
                <Th>{t('team.units.column.parent')}</Th>
                <Th num>{t('team.units.column.members')}</Th>
                <Th>{t('team.units.column.status')}</Th>
                {canManage ? <Th>{t('team.units.column.actions')}</Th> : null}
              </Tr>
            </THead>
            <TBody>
              {units.map((unit) => {
                const parent =
                  unit.parentId === null
                    ? null
                    : (units.find((candidate) => candidate.id === unit.parentId) ?? null);
                return (
                  <Tr key={unit.id} data-testid={`team-units-row-${unit.id}`}>
                    <Td heading>
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate font-medium text-paper">{unit.name}</span>
                        {unit.path !== unit.name ? (
                          <span className="truncate font-mono text-micro text-paper-low">
                            {unit.path}
                          </span>
                        ) : null}
                      </span>
                    </Td>
                    <Td label={t('team.units.column.parent')}>
                      <span className="font-mono text-micro text-paper-mid">
                        {parent?.path ?? t('team.units.root_parent')}
                      </span>
                    </Td>
                    <Td num label={t('team.units.column.members')}>
                      {unit.memberCount}
                    </Td>
                    <Td label={t('team.units.column.status')}>
                      <Chip
                        tone={unit.status === 'active' ? 'ok' : 'neutral'}
                        lamp={false}
                        testId={`team-units-status-${unit.id}`}
                      >
                        {unit.status === 'active'
                          ? t('team.units.status.active')
                          : t('team.units.status.archived')}
                      </Chip>
                    </Td>
                    {canManage ? (
                      <Td label={t('team.units.column.actions')} stack>
                        <div className="flex items-center justify-end gap-1.5 max-md:justify-start">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDrawer({ mode: 'edit', unit })}
                            data-testid={`team-units-edit-${unit.id}`}
                          >
                            {t('team.units.edit')}
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => {
                              setArchiveError(null);
                              setArchiveTarget(unit);
                            }}
                            data-testid={`team-units-archive-${unit.id}`}
                          >
                            {t('team.units.archive')}
                          </Button>
                        </div>
                      </Td>
                    ) : null}
                  </Tr>
                );
              })}
            </TBody>
          </Table>
        )}
      </Panel>

      {drawer !== null && orgId !== undefined ? (
        <UnitDrawer orgId={orgId} state={drawer} units={units} onClose={() => setDrawer(null)} />
      ) : null}

      {archiveTarget !== null ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open && !archiveM.isPending) {
              setArchiveTarget(null);
              setArchiveError(null);
            }
          }}
          tone="danger"
          title={t('team.units.archive_title')}
          body={
            <div className="flex flex-col gap-2">
              <p className="m-0">{t('team.units.archive_body', { unit: archiveTarget.path })}</p>
              {archiveError !== null ? (
                <p
                  role="alert"
                  data-testid="team-units-archive-error"
                  className="m-0 text-label text-bad"
                >
                  {archiveError}
                </p>
              ) : null}
            </div>
          }
          confirmLabel={t('team.units.archive_confirm')}
          cancelLabel={t('team.units.archive_cancel')}
          closeLabel={t('team.units.drawer.close')}
          loading={archiveM.isPending}
          testId="team-units-archive-dialog"
          onConfirm={() => {
            void handleArchive();
          }}
        />
      ) : null}
    </section>
  );
}

interface UnitDrawerProps {
  readonly orgId: string;
  readonly state: DrawerState;
  readonly units: readonly OrgUnitSummary[];
  readonly onClose: () => void;
}

function UnitDrawer({ orgId, state, units, onClose }: UnitDrawerProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const createM = useCreateOrgUnit();
  const updateM = useUpdateOrgUnit();
  const pending = createM.isPending || updateM.isPending;
  const editing = state.mode === 'edit' ? state.unit : null;

  const [name, setName] = useState(editing?.name ?? '');
  const [parentId, setParentId] = useState(editing?.parentId ?? ROOT);
  const [nameError, setNameError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const parentOptions = [
    { value: ROOT, label: t('team.units.root_parent') },
    ...units
      .filter((candidate) => editing === null || candidate.id !== editing.id)
      .map((candidate) => ({ value: candidate.id, label: candidate.path })),
  ];

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setNameError(null);
    setFormError(null);
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setNameError(t('team.units.form.name_required'));
      return;
    }
    const body = { name: trimmed, parentId: parentId === ROOT ? null : parentId };
    try {
      if (editing === null) {
        await createM.mutateAsync({ orgId, body });
        toast.success(t('team.units.toast.created'));
      } else {
        await updateM.mutateAsync({ orgId, orgUnitId: editing.id, body });
        toast.success(t('team.units.toast.saved'));
      }
      onClose();
    } catch (err) {
      setFormError(formatTeamApiError(err, t('team.units.error.save')));
    }
  }

  return (
    <Drawer
      open
      onOpenChange={(open) => {
        if (!open && !pending) onClose();
      }}
      width="sm"
      title={
        editing === null ? t('team.units.drawer.create_title') : t('team.units.drawer.edit_title')
      }
      description={t('team.units.drawer.description')}
      closeLabel={t('team.units.drawer.close')}
      testId="team-units-drawer"
    >
      <form
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
        className="flex flex-col gap-4"
      >
        <FormField
          label={t('team.units.form.name_label')}
          fieldId="team-units-form-name"
          required
          error={nameError}
        >
          <Input
            id="team-units-form-name"
            value={name}
            maxLength={120}
            disabled={pending}
            invalid={nameError !== null}
            aria-describedby={nameError !== null ? 'team-units-form-name-error' : undefined}
            placeholder={t('team.units.form.name_placeholder')}
            onChange={(event) => {
              setName(event.currentTarget.value);
              setNameError(null);
            }}
            data-testid="team-units-form-name"
          />
        </FormField>

        <FormField label={t('team.units.form.parent_label')} fieldId="team-units-form-parent">
          <Select
            value={parentId}
            onValueChange={setParentId}
            options={parentOptions}
            disabled={pending}
            ariaLabel={t('team.units.form.parent_label')}
            testId="team-units-form-parent"
          />
        </FormField>

        {formError !== null ? (
          <p role="alert" data-testid="team-units-form-error" className="m-0 text-label text-bad">
            {formError}
          </p>
        ) : null}

        <div className="mt-1 flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={pending}
            data-testid="team-units-form-cancel"
          >
            {t('team.units.form.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={pending}
            data-testid="team-units-form-submit"
          >
            {editing === null
              ? t('team.units.form.submit_create')
              : t('team.units.form.submit_save')}
          </Button>
        </div>
      </form>
    </Drawer>
  );
}
